/**
 * Clients and the places Majik cleans for them. A client is one billing
 * account (one QuickBooks customer); a location is one site — an office, a
 * store, a floor, an apartment — and jobs, cleaners and orders attach to the
 * location (docs/majik/DATA-ARCHITECTURE-SPEC.md → People & accounts). Pure.
 */
import { z } from 'zod'
import { cadences } from '../shared/cadence.ts'
import { recordIdSchema } from '../shared/record-id.ts'

export const taxCodes = ['tax', 'non'] as const
export type TaxCode = (typeof taxCodes)[number]

export const paymentTerms = ['due_on_receipt', 'net_10', 'net_30'] as const
export type PaymentTerms = (typeof paymentTerms)[number]

export const paymentTermLabels: Record<PaymentTerms, string> = {
  due_on_receipt: 'Due on receipt',
  net_10: 'Net 10',
  net_30: 'Net 30',
}

/** A location's service state; a client's is the rollup of its locations'. */
/**
 * How a client is billed (the client page's Billing setup): a flat monthly
 * rate, visits rolled into a monthly invoice, or each visit invoiced on its own.
 */
export const billingMethods = ['monthly_rate', 'per_visit', 'each_visit'] as const
export type BillingMethod = (typeof billingMethods)[number]

export const billingMethodLabels: Record<BillingMethod, string> = {
  monthly_rate: 'Flat monthly rate',
  per_visit: 'Per visit',
  each_visit: 'Each visit invoiced',
}

export const serviceStatuses = ['active', 'paused', 'unassigned', 'inactive'] as const
export type ServiceStatus = (typeof serviceStatuses)[number]

export const serviceStatusLabels: Record<ServiceStatus, string> = {
  active: 'Active',
  paused: 'Paused',
  unassigned: 'No cleaner yet',
  inactive: 'Inactive',
}

export const billingCadences = ['monthly', 'quarterly', 'semi_annual', 'yearly'] as const

export const clientSchema = z.object({
  id: recordIdSchema,
  /** As it appears on invoices. */
  name: z.string().trim().min(1, 'Give the client a name.').max(160),
  taxCode: z.enum(taxCodes),
  paymentTerms: z.enum(paymentTerms),
  billingMethod: z.enum(billingMethods),
  /** Empty until the client is connected to QuickBooks. Never shown on the list. */
  quickbooksCustomerId: z.string().nullable().default(null),
  /** Where invoices are mailed, free text as Monday had it; null when not given. */
  billingAddress: z.string().trim().max(400).nullable().default(null),
  /** Internal; the client never sees it. */
  notes: z.string().max(4000).default(''),
  /** Billing setup ⋯ Pause reminders: no payment reminders go out on its invoices. */
  paymentRemindersPaused: z.boolean().default(false),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  deletedAt: z.iso.datetime().nullable().default(null),
})
export type Client = z.infer<typeof clientSchema>

export const locationSchema = z.object({
  id: recordIdSchema,
  clientId: recordIdSchema,
  name: z.string().trim().min(1, 'Give the location a name.').max(160),
  status: z.enum(serviceStatuses),
  addressLine1: z.string().trim().min(1, 'Add the street address.'),
  addressLine2: z.string().nullable().default(null),
  /**
   * City, state and ZIP may be blank on an imported location — Monday often
   * has only a street (D-037). The location form still requires them.
   */
  city: z.string().trim().min(1).nullable().default(null),
  state: z.string().length(2).nullable().default(null),
  postalCode: z
    .string()
    .regex(/^\d{5}$/, 'A ZIP code is five digits.')
    .nullable()
    .default(null),
  /** Printed on every assigned cleaner's schedule. */
  workNotes: z.string().max(4000).default(''),
  /** Keys, alarm codes, the entrance — kept off schedules. */
  accessNotes: z.string().max(4000).default(''),
  serviceFrequency: z.enum(cadences).nullable().default(null),
  /** When the next monthly invoice is built. */
  nextBillDate: z.iso.date().nullable().default(null),
  billingCadence: z.enum(billingCadences).default('monthly'),
  /** May get the "it has been a while" reminder. */
  remindersEnabled: z.boolean().default(true),
  /** The day the last "it has been a while" reminder went out; null when none has. */
  lastReminderOn: z.iso.date().nullable().default(null),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  deletedAt: z.iso.datetime().nullable().default(null),
})
export type Location = z.infer<typeof locationSchema>

export const contactRoles = ['point_of_contact', 'billing', 'other'] as const
export type ContactRole = (typeof contactRoles)[number]

/** A person on a client, optionally on one location only, with what they are for. */
export const clientPersonSchema = z.object({
  id: recordIdSchema,
  clientId: recordIdSchema,
  locationId: recordIdSchema.nullable().default(null),
  contactId: recordIdSchema,
  role: z.enum(contactRoles),
  /** One primary per client, location and role: the one notices and invoices go to first. */
  isPrimary: z.boolean(),
})
export type ClientPerson = z.infer<typeof clientPersonSchema>

/** "Main contact", "Contact", "Billing", "Other" — the one tag a person shows. */
export function personRoleLabel(person: Pick<ClientPerson, 'role' | 'isPrimary'>): string {
  if (person.role === 'billing') return 'Billing'
  if (person.role === 'other') return 'Other'
  return person.isPrimary ? 'Main contact' : 'Contact'
}

/** What a client is charged every month, copied onto the monthly invoice. */
export const billingItemSchema = z.object({
  id: recordIdSchema,
  clientId: recordIdSchema,
  locationId: recordIdSchema.nullable().default(null),
  itemName: z.string().trim().min(1).max(200),
  unitPrice: z.number(),
  quantity: z.number().positive(),
  active: z.boolean(),
  sortOrder: z.number().int().default(0),
})
export type BillingItem = z.infer<typeof billingItemSchema>

export function lineTotal(line: { unitPrice: number; quantity: number }): number {
  return Math.round(line.unitPrice * line.quantity * 100) / 100
}

/** "137 Wooster Street, New York, NY 10012" — one line, for messages and screens. */
export function fullAddress(
  location: Pick<Location, 'addressLine1' | 'addressLine2' | 'city' | 'state' | 'postalCode'>,
): string {
  const street = [location.addressLine1, location.addressLine2].filter(Boolean).join(', ')
  const region = [location.state, location.postalCode].filter(Boolean).join(' ')
  return [street, location.city, region].filter(Boolean).join(', ')
}

/**
 * A client's status, from its locations: active if any is, else paused, else
 * waiting for a cleaner, else inactive (spec → `clients.status`).
 */
export function clientStatus(locations: readonly Pick<Location, 'status'>[]): ServiceStatus {
  for (const status of ['active', 'paused', 'unassigned'] as const) {
    if (locations.some((location) => location.status === status)) return status
  }
  return 'inactive'
}

/**
 * The person a location's notices go to: its own primary point of contact,
 * else the client's (spec → `jobs.point_of_contact`).
 */
export function pointOfContact(
  people: readonly ClientPerson[],
  location: Pick<Location, 'id' | 'clientId'>,
): ClientPerson | null {
  const pocs = people.filter(
    (p) => p.role === 'point_of_contact' && p.clientId === location.clientId,
  )
  const rank = (p: ClientPerson) => (p.locationId === location.id ? 0 : p.locationId ? 2 : 1)
  const candidates = pocs.filter((p) => p.locationId === location.id || p.locationId === null)
  return (
    [...candidates].sort(
      (a, b) => rank(a) - rank(b) || Number(b.isPrimary) - Number(a.isPrimary),
    )[0] ?? null
  )
}
